import {
    Condition,
    ConditionType,
    IValueInterval,
    PredefinedFilter,
} from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { formatDateToDateString } from "@components/inputs/date/utils";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { HIERARCHY_EMPTY_VALUE_ID_PREFIX } from "@components/smart/smartSelect/SmartLabelSelect";
import { CUSTOM_DATE_RANGE_ID } from "@pages/reports/customFilterComponents/ComposedDateRange.utils";
import { isObjectEmpty } from "@utils/general";

import { TRecordAny, TRecordType, TValue } from "../../../global.types";
import BindingContext from "../../../odata/BindingContext";
import { AccountTypeCode } from "../../../odata/GeneratedEnums";
import { AccountAnalysisProps } from "../accountAnalysis/AccountAnalysisDef";
import { CommonReportProps } from "../CommonDefs";
import { IReportDateRange, labelColumnNamePrefix, ReportColumnType, } from "../Report.utils";
import { ReportStorage } from "../ReportStorage";

const AA_DRILL_DOWN_GROUP_COLUMNS = new Set(["Account_Synthetic", "Account_Analytic"]);

type EandRRowKind = "account" | "sectionTotal" | "grandTotal" | "label" | "other";

const parseAccountNumber = (accountValue?: string): string | null => accountValue?.split(" ")[0] ?? null;

export const resolveDateRangeCustomValue = (storage: ReportStorage): IValueInterval | undefined => {
    const dateRangeKey = storage.data.entity[CommonReportProps.dateRange] as string;
    if (dateRangeKey === CUSTOM_DATE_RANGE_ID) {
        return storage.data.entity[CommonReportProps.dateRangeCustomValue] as IValueInterval;
    }
    const namedRange = storage.data.customData.namedDateRanges?.find((dr: IReportDateRange) => dr.Key === dateRangeKey);
    if (namedRange) {
        return {
            from: formatDateToDateString(namedRange.Value.DateStart),
            to: formatDateToDateString(namedRange.Value.DateEnd),
        };
    }
    return undefined;
};

export const buildAccountFilter = (
    entity: TRecordAny,
    isSectionTotalRow: boolean,
    isGrandTotalRow: boolean,
    storage?: ReportStorage
): TRecordAny | null => {
    const exactAccountNumber =
        (entity["Account_Number"] as string) ?? parseAccountNumber(entity["Account_Analytic"] as string);
    const accountNumber = exactAccountNumber ?? parseAccountNumber(entity["Account_Synthetic"] as string);

    if (accountNumber) {
        return {
            [BindingContext.localContext("Account_Number")]: [
                {
                    id: "Account_Number",
                    type: ConditionType.Included,
                    condition: exactAccountNumber ? Condition.Equals : Condition.BeginsWith,
                    filter: PredefinedFilter.Value,
                    value: accountNumber,
                },
            ],
        };
    }
    if (isSectionTotalRow) {
        return { [AccountAnalysisProps.accountType]: resolveAccountTypeCode(entity, storage) };
    }
    if (isGrandTotalRow) {
        return { [AccountAnalysisProps.accountType]: [AccountTypeCode.Expense, AccountTypeCode.Income] };
    }
    return null;
};

const resolveAccountTypeCode = (entity: TRecordAny, storage?: ReportStorage): AccountTypeCode | undefined => {
    if (entity["Account_TypeCode"]) {
        return entity["Account_TypeCode"] as AccountTypeCode;
    }
    const topLevelRows: TRecordAny[] = storage?.tableAPI?.getState().data?.Rows ?? [];
    const sectionGroupNode = topLevelRows.find((row: TRecordAny) => row.Value?.AccountType_Name === entity["AccountType_Name"]);
    return sectionGroupNode?.Value?.Account_TypeCode as AccountTypeCode | undefined;
};

export const buildSideFilter = (isDebitColumn: boolean, isCreditColumn: boolean): TRecordAny => {
    if (!isDebitColumn && !isCreditColumn) {
        return {};
    }
    const sideColumnAlias = isDebitColumn ? "JournalEntry_Debit" : "JournalEntry_Credit";
    return {
        [BindingContext.localContext(sideColumnAlias)]: [
            {
                id: sideColumnAlias,
                type: ConditionType.Excluded,
                condition: Condition.Equals,
                filter: PredefinedFilter.Value,
                value: 0,
            },
        ],
    };
};

const isUngroupedAccountSummaryRow = (entity: TRecordAny, groupColumnAlias: string): boolean => {
    if (groupColumnAlias) {
        return false;
    }

    const hasAccount = !!entity["Account_Analytic"] || !!entity["Account_Synthetic"];
    const isDocumentDetail = !!entity["Document_NumberOurs"] || !!entity["JournalEntry_DateAccountingTransaction"];

    return hasAccount && !isDocumentDetail;
};

export const classifyRow = (entity: TRecordAny, groupColumnAlias: string): EandRRowKind => {
    // Total rows share GroupColumnAlias values with regular rows - check them first.
    if (groupColumnAlias === "Account_Synthetic" && !entity["Account_Synthetic"] && !!entity["AccountType_Name"]) {
        return "sectionTotal";
    }
    if (groupColumnAlias === "AccountType_Name" && !entity["AccountType_Name"]) {
        return "grandTotal";
    }
    if (AA_DRILL_DOWN_GROUP_COLUMNS.has(groupColumnAlias)) {
        return "account";
    }
    if (groupColumnAlias?.startsWith(labelColumnNamePrefix)) {
        return "label";
    }
    if (isUngroupedAccountSummaryRow(entity, groupColumnAlias)) {
        return "account";
    }
    return "other";
};

export const getLabelParameterDrilldownFilters = (storage: ReportStorage, labelPath: string): TRecordType<string[]> => {
    const result: TRecordType<string[]> = {};
    const labelsParamValue = storage.settings[labelPath] as IReportLabelsParameter;

    if (isObjectEmpty(labelsParamValue?.SelectedLabels) && !labelsParamValue?.EmptyLabelHierarchyIds?.length) {
        return result;
    }

    const labelColumns = (storage.supportedColumns ?? []).filter((col) => col.Type === ReportColumnType.Label);

    if (!labelColumns.length) {
        return result;
    }

    const labelsInfo = storage.getInfo(storage.data.bindingContext.navigate(labelPath));
    const items = (labelsInfo?.fieldSettings?.items ?? []) as ISelectItem[];

    for (const [hierarchyId, ids] of Object.entries(labelsParamValue?.SelectedLabels ?? {})) {
        if (!ids?.length) {
            continue;
        }
        const column = labelColumns.find((col) => col.ColumnAlias === `${labelColumnNamePrefix}${hierarchyId}`);

        if (column) {
            result[BindingContext.localContext(column.ColumnAlias)] =
                ids.map((id) => {
                    return items
                        .filter(item => !item.id.toString().startsWith(HIERARCHY_EMPTY_VALUE_ID_PREFIX))
                        .find(item => item.id.toString().split("_")[1] === id.toString())?.label;
                });
        }
    }

    for (const hierarchyId of labelsParamValue?.EmptyLabelHierarchyIds ?? []) {
        const column = labelColumns.find((col) => col.ColumnAlias === `${labelColumnNamePrefix}${hierarchyId}`);
        if (column) {
            result[BindingContext.localContext(column.ColumnAlias)] = [...(result[BindingContext.localContext(column.ColumnAlias)] ?? []), null];
        }
    }

    return result;
};

interface IReportLabelsParameter {
    SelectedLabels: TRecordType<number[]>;
    EmptyLabelHierarchyIds: number[];
}

export const transformLabelsHierarchyFilter = (value: TValue): TValue => {
    const typedValue = value as string[] | undefined;
    const res: IReportLabelsParameter = {
        SelectedLabels: {},
        EmptyLabelHierarchyIds: []
    };

    for (const v of typedValue ?? []) {
        // v is in format: {hierarchyId}_{labelId}, e.g. 1_2, 1_3, 2_4, 2_5
        // or in format: {HIERARCHY_EMPTY_VALUE_ID_PREFIX}{hierarchyId} for empty values for each hierarchy
        const valArray = v.split("_");

        if (valArray[0] === HIERARCHY_EMPTY_VALUE_ID_PREFIX) {
            res.EmptyLabelHierarchyIds.push(Number(valArray[1]));
            continue;
        }

        const hierarchyId = Number(valArray[0]);
        const labelId = Number(valArray[1]);

        if (!res.SelectedLabels[hierarchyId]) {
            res.SelectedLabels[hierarchyId] = [];
        }
        res.SelectedLabels[hierarchyId].push(labelId);
    }
    return res;
};